import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';

import { CategoryCard } from 'homepage/components/categoryListSection/CategoryCard';
import { getSearchLandingFeedSubSectionTrackingInfo } from 'homepage/utils/getSearchLandingFeedSubsectionTrackingInfo';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { HomepageTabActions } from 'homepage/state/homepageTabReducer';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { ISubSection } from 'homepage/types/Feed';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useIsElementInViewport } from 'shared/hooks';
import { useHomepageTabDispatch } from 'homepage/hooks/useHomepageTab';

interface ICategoryListSection {
  constructorUserParams?: IConstructorUserParams;
  moduleTrackingData: IModuleTrackingData;
  sectionIndex: number;
  subsections: ISubSection[];
}

export const CategoryListSection = ({
  constructorUserParams,
  moduleTrackingData,
  sectionIndex,
  subsections,
}: ICategoryListSection) => {
  const homepageDispatch = useHomepageTabDispatch();
  const moduleRef = useRef();
  const initialInViewport = useIsElementInViewport(moduleRef);

  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const [currentSubsections, setCurrentSubSections] = useState(subsections);

  const handleSubSectionClick = (index = 0, subsection) => {
    sendTrackingEvent('CMS_MODULE_TAP', {
      ...getSearchLandingFeedSubSectionTrackingInfo(moduleTrackingData, subsection, index),
    });
  };

  const handleSubsectionUpdate = (subsectionIndex, constructorResponse) => {
    if (currentSubsections?.length > 0) {
      const newSubsections = [...currentSubsections];
      newSubsections[subsectionIndex] = {
        ...currentSubsections[subsectionIndex],
        constructorResponse,
      };
      setCurrentSubSections(newSubsections);
      homepageDispatch({
        type: HomepageTabActions.setCurrentSearchLandingFeedData,
        payload: { index: sectionIndex, subsections: newSubsections },
      });
    }
  };

  useEffect(() => {
    if (!isModuleInViewport) {
      setIsModuleInViewport(initialInViewport);
    }
  }, [initialInViewport, isModuleInViewport]);

  useEffect(() => {
    homepageDispatch({
      type: HomepageTabActions.setCurrentSearchLandingFeedData,
      payload: { index: sectionIndex, subsections: currentSubsections },
    });
  }, [currentSubsections]);

  return (
    <CategoryListSection.Container ref={moduleRef}>
      {subsections?.map((section, index) => (
        <CategoryCard
          constructorUserParams={constructorUserParams}
          key={section?.id}
          subsectionIndex={index}
          isModuleInViewport={isModuleInViewport}
          {...section}
          onClick={() => handleSubSectionClick(index, section)}
          handleSubsectionUpdate={handleSubsectionUpdate}
        />
      ))}
    </CategoryListSection.Container>
  );
};

CategoryListSection.Container = styled.div`
  display: flex;
  margin: 32px 0px;
  width: 100vw;
  max-width: 100%;
  padding: 0px 8px;
  ${hideScrollbars}

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-wrap: wrap;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 30px 32px;
    width: 100vw;
  }
`;
